import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, X, Send, Loader2, User, ChevronDown, ChevronUp } from 'lucide-react';
import './AdminChatWidget.css';

const AdminChatWidget = () => {
  const { isAuthenticated, user } = useAuth();
  const {
    conversations,
    activeChat,
    messages,
    loading,
    selectChat,
    sendMessage,
    sendTyping,
    getTotalUnreadCount,
    fetchConversations,
    socket
  } = useChat();

  const [isOpen, setIsOpen] = useState(false);
  // Default to showing full conversations list so sidebar isn't visually "cut off"
  const [showConversations, setShowConversations] = useState(true);
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const unreadCount = getTotalUnreadCount();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current && activeChat) {
      inputRef.current.focus();
    }
  }, [isOpen, activeChat]);

  // Fetch conversations when widget opens
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen, fetchConversations]);

  // Ensure conversations are fetched on mount and periodically refreshed
  // This ensures the badge updates in real-time even when widget is closed
  useEffect(() => {
    // Initial fetch
    if (isAuthenticated && user?.role === 'admin') {
      fetchConversations();
    }

    // Set up interval to refresh conversations every 5 seconds
    // This ensures the badge updates when new messages arrive
    // The ChatContext also updates on socket events, but this is a backup
    const interval = setInterval(() => {
      if (isAuthenticated && user?.role === 'admin') {
        fetchConversations();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isAuthenticated, user?.role, fetchConversations]);

  // Select first conversation if none selected
  useEffect(() => {
    if (isOpen && !activeChat && conversations.length > 0) {
      selectChat(conversations[0]);
    }
  }, [isOpen, conversations, activeChat, selectChat]);

  // Handle opening chat
  const handleOpenChat = () => {
    setIsOpen(true);
    fetchConversations();
  };

  // Handle selecting a conversation
  const handleSelectConversation = async (conversation) => {
    await selectChat(conversation);
    setShowConversations(false);
  };

  // Handle sending message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageInput.trim() || !activeChat) return;

    sendMessage(messageInput);
    setMessageInput('');
    setIsTyping(false);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    sendTyping(false);
  };

  // Handle typing indicator
  const handleInputChange = (e) => {
    setMessageInput(e.target.value);
    
    if (!isTyping && e.target.value.trim()) {
      setIsTyping(true);
      sendTyping(true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTyping(false);
    }, 2000);
  };

  // Format message time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  // Get customer name
  const getCustomerName = () => {
    if (!activeChat?.otherParticipant) return 'Customer';
    const { firstName, lastName } = activeChat.otherParticipant;
    return `${firstName} ${lastName}`;
  };

  // Only show for admins
  if (!isAuthenticated || user?.role !== 'admin') return null;

  return (
    <>
      {/* Chat Button */}
      <button
        className="admin-chat-widget-button"
        onClick={handleOpenChat}
        aria-label="Open admin chat"
      >
        <MessageCircle size={24} />
        {unreadCount > 0 && (
          <span className="admin-chat-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="admin-chat-window">
          <div className="admin-chat-window-header">
            <div className="admin-chat-header-info">
              <h3>Customer Chats</h3>
              <span className="admin-chat-count">{conversations.length}</span>
            </div>
            <button
              className="admin-chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          <div className="admin-chat-content">
            {/* Conversations Sidebar */}
            <div className="admin-chat-conversations">
              <div className="admin-chat-conversations-header">
                <span>Conversations</span>
                <button
                  className="admin-chat-toggle-conversations"
                  onClick={() => setShowConversations(!showConversations)}
                >
                  {showConversations ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
              <div className={`admin-chat-conversations-list ${showConversations ? 'expanded' : ''}`}>
                {loading && conversations.length === 0 ? (
                  <div className="admin-chat-loading">
                    <Loader2 className="spinning" size={20} />
                    <p>Loading...</p>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="admin-chat-empty">
                    <MessageCircle size={32} />
                    <p>No conversations</p>
                  </div>
                ) : (
                  conversations.map((conversation) => {
                    const isActive = activeChat?._id === conversation._id;
                    const customer = conversation.otherParticipant;
                    const customerName = customer 
                      ? `${customer.firstName} ${customer.lastName}`
                      : 'Customer';
                    
                    return (
                      <div
                        key={conversation._id}
                        className={`admin-chat-conversation-item ${isActive ? 'active' : ''}`}
                        onClick={() => handleSelectConversation(conversation)}
                      >
                        <div className="admin-chat-conversation-avatar">
                          <User size={16} />
                        </div>
                        <div className="admin-chat-conversation-info">
                          <div className="admin-chat-conversation-name">{customerName}</div>
                          <div className="admin-chat-conversation-preview">
                            {conversation.lastMessage?.content || 'No messages yet'}
                          </div>
                        </div>
                        {conversation.unreadCount > 0 && (
                          <span className="admin-chat-conversation-unread">{conversation.unreadCount}</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Messages Area */}
            <div className="admin-chat-messages-area">
              {activeChat ? (
                <>
                  <div className="admin-chat-messages-header">
                    <div className="admin-chat-messages-header-info">
                      <h4>{getCustomerName()}</h4>
                      {socket?.connected && (
                        <span className="admin-chat-status online">Live chat connected</span>
                      )}
                    </div>
                  </div>

                  <div className="admin-chat-messages-container">
                    {loading && messages.length === 0 ? (
                      <div className="admin-chat-loading">
                        <Loader2 className="spinning" size={24} />
                        <p>Loading messages...</p>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="admin-chat-empty">
                        <MessageCircle size={48} />
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      <div className="admin-chat-messages">
                        {messages.map((message) => {
                          const messageId = message._id?.toString() || message._id || message.tempId;
                          const senderId = message.sender?._id?.toString() || message.sender?._id;
                          const userId = user?._id?.toString() || user?._id;
                          const isOwnMessage = senderId === userId;
                          const isSystemMessage = message.type === 'system';
                          
                          return (
                            <div
                              key={messageId}
                              className={`admin-chat-message ${isSystemMessage ? 'system' : isOwnMessage ? 'own' : 'other'}`}
                            >
                              <div className="admin-chat-message-content">
                                {isSystemMessage && (
                                  <div className="system-message-label">Automated Reply</div>
                                )}
                                <p>{message.content}</p>
                                <span className="admin-chat-message-time">{formatTime(message.createdAt)}</span>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>

                  <form className="admin-chat-input-container" onSubmit={handleSendMessage}>
                    <input
                      ref={inputRef}
                      type="text"
                      value={messageInput}
                      onChange={handleInputChange}
                      placeholder="Type a message..."
                      className="admin-chat-input"
                      disabled={!socket?.connected}
                    />
                    <button
                      type="submit"
                      className="admin-chat-send-btn"
                      disabled={!messageInput.trim() || !socket?.connected}
                    >
                      <Send size={20} />
                    </button>
                  </form>
                </>
              ) : (
                <div className="admin-chat-placeholder">
                  <MessageCircle size={64} />
                  <h3>Select a conversation</h3>
                  <p>Choose a customer from the list to start chatting</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminChatWidget;

